import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, EyeOff, Eye, Check, Loader2, Search, AlertTriangle, Sparkles, Trash2, DollarSign, Tags, ArrowRight, History } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";
import { categories } from "../../mock";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { thumb } from "../../utils/image";
import UndoAction from "../UndoAction";
import { ArchiveReasonPicker, composeReason } from "./ArchiveReasons";
import { placesLabel } from "./AdminAccounts";

const Signal = ({ ok, warn, label }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
    warn ? "bg-secondary text-muted-foreground"
      : ok ? "bg-[#16a34a]/12 text-[#15803d]" : "bg-rose-100 text-rose-600"}`}>
    {warn ? <AlertTriangle size={10} /> : ok ? <Check size={10} /> : <AlertTriangle size={10} />} {label}
  </span>
);

const DealRow = ({ d, children }) => (
  <div className="border border-border rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid={`review-row-${d.id}`}>
    {d.image && <img src={thumb(d.image, 96)} alt="" loading="lazy" decoding="async" className="h-14 w-14 rounded-xl object-cover" />}
    <div className="flex-1 min-w-[180px]">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to={`/deal/${d.id}`} className="font-bold text-foreground hover:text-[#16a34a] truncate">{d.title}</Link>
        {d.hidden && <span className="text-[11px] font-bold bg-rose-100 text-rose-600 px-2 py-0.5 rounded-full">Hidden</span>}
        {!!d.discount && <span className="text-[11px] font-bold bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">{d.discount}% off</span>}
      </div>
      <p className="text-xs text-muted-foreground truncate">{d.business} · {d.location || "—"}</p>
      {d.shop && (
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`shop-signals-${d.id}`}>
          <Signal ok={d.shop.emailVerified} label={d.shop.emailVerified ? "Email confirmed" : "Email unconfirmed"} />
          <Signal ok={d.shop.domainVerified}
            label={d.shop.domainVerified ? `Own domain · ${d.shop.verifiedDomain}` : "Free webmail address"} />
          <Signal ok={d.shop.placesMatch?.matched > 0} warn={!d.shop.placesMatch}
            label={placesLabel(d.shop.placesMatch)} />
          {d.shop.joinedAt && <span className="text-[11px] text-muted-foreground">joined {String(d.shop.joinedAt).slice(0, 10)}</span>}
        </div>
      )}
      {(d.flags || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {d.flags.map((f) => (
            <span key={f.code} data-testid={`flag-${d.id}-${f.code}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
              <AlertTriangle size={11} /> {f.label}
            </span>
          ))}
        </div>
      )}
      {(d.reports || []).length > 0 && (
        <p className="text-xs text-amber-700 mt-1" data-testid={`reports-${d.id}`}>
          Reports: {d.reports.map((r) => r.reason).join(", ")}
        </p>
      )}
      {d.hiddenReason && <p className="text-xs text-rose-600 mt-1">Removed: {d.hiddenReason}</p>}
    </div>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

export const AdminDealReview = () => {
  const { toast } = useToast();
  const [flagged, setFlagged] = useState([]);
  const [pending, setPending] = useState([]);
  const [results, setResults] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [aiOn, setAiOn] = useState(false);
  const [capsOn, setCapsOn] = useState(true);
  const [caps, setCaps] = useState({});
  const [capsDefault, setCapsDefault] = useState(0);
  const [showCaps, setShowCaps] = useState(false);
  const [savingCaps, setSavingCaps] = useState(false);
  const [pullFor, setPullFor] = useState(null);
  const [pullReason, setPullReason] = useState("");
  const [pullPicked, setPullPicked] = useState([]);
  const togglePullReason = (r) => setPullPicked((p) => (p.includes(r) ? p.filter((x) => x !== r) : [...p, r]));
  const [deleteFor, setDeleteFor] = useState(null);
  const [recats, setRecats] = useState([]);

  const loadFlagged = useCallback(async () => {
    try {
      const [f, p, rc] = await Promise.all([
        api.get("/admin/deals/flagged"),
        api.get("/admin/deals/pending"),
        api.get("/admin/deals/recategorizations"),
      ]);
      setFlagged(f.data); setPending(p.data); setRecats(rc.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadFlagged(); api.get("/admin/settings/moderation").then((r) => setAiOn(r.data.enabled)).catch(() => {}); api.get("/admin/settings/price-caps").then((r) => { setCapsOn(r.data.enabled); setCaps(r.data.caps || {}); setCapsDefault(r.data.default || 0); }).catch(() => {}); }, [loadFlagged]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { setResults((await api.get(`/admin/deals?q=${encodeURIComponent(q.trim())}`)).data); } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const refresh = async () => {
    await loadFlagged();
    if (q.trim()) {
      try { setResults((await api.get(`/admin/deals?q=${encodeURIComponent(q.trim())}`)).data); } catch { /* ignore */ }
    }
  };

  const clearFlags = async (id) => {
    setBusy(id + "ok");
    try { await api.post(`/admin/deals/${id}/review`, { action: "ok" }); toast({ title: "Marked as fine" }); await refresh(); }
    catch { toast({ title: "Action failed" }); }
    finally { setBusy(""); }
  };

  const pull = async (reasonText) => {
    const d = pullFor;
    setPullFor(null);
    setBusy(d.id + "hide");
    try { await api.post(`/admin/deals/${d.id}/review`, { action: "hide", reason: reasonText }); toast({ title: "Deal archived", description: d.title }); await refresh(); }
    catch { toast({ title: "Action failed" }); }
    finally { setBusy(""); setPullReason(""); setPullPicked([]); }
  };

  const restore = async (d) => {
    setBusy(d.id + "unhide");
    try { await api.post(`/admin/deals/${d.id}/unhide`); toast({ title: "Deal restored" }); await refresh(); }
    catch { toast({ title: "Action failed" }); }
    finally { setBusy(""); }
  };

  const undelete = async (id) => {
    try { await api.post(`/admin/deals/${id}/undelete`); toast({ title: "Deal brought back" }); await refresh(); }
    catch (e) { toast({ title: "Could not undo", description: e.response?.data?.detail || "It may already be purged." }); }
  };

  const destroy = async () => {
    const d = deleteFor;
    setDeleteFor(null);
    setBusy(d.id + "delete");
    try {
      await api.delete(`/admin/deals/${d.id}`);
      toast({
        title: "Deal deleted",
        description: d.title,
        action: <UndoAction onUndo={() => undelete(d.id)} testid="undo-delete-deal" />,
      });
      await refresh();
    } catch { toast({ title: "Delete failed" }); }
    finally { setBusy(""); }
  };

  const toggleAi = async () => {
    try {
      const { data } = await api.post("/admin/settings/moderation", { enabled: !aiOn });
      setAiOn(data.enabled);
      toast({ title: data.enabled ? "AI content check ON" : "AI content check OFF",
        description: data.enabled ? "New deals are screened for adult/graphic content at submit." : "Deals publish without AI screening." });
    } catch { toast({ title: "Could not change the setting" }); }
  };

  const toggleCaps = async () => {
    try {
      const { data } = await api.post("/admin/settings/price-caps", { enabled: !capsOn });
      setCapsOn(data.enabled);
      toast({ title: data.enabled ? "Price caps ON" : "Price caps OFF",
        description: data.enabled ? "Deals with an unrealistic price for their category are blocked at submit." : "Shops can post any price." });
    } catch { toast({ title: "Could not change the setting" }); }
  };

  const saveCaps = async () => {
    setSavingCaps(true);
    try {
      const cleaned = {};
      Object.entries(caps).forEach(([k, v]) => { const n = Number(v); if (n > 0) cleaned[k] = n; });
      const { data } = await api.post("/admin/settings/price-caps", { enabled: capsOn, caps: cleaned, default: Number(capsDefault) || undefined });
      setCaps(data.caps || {}); setCapsDefault(data.default || 0);
      toast({ title: "Price ceilings saved", description: "Amounts are in USD; each deal's price is converted from its own currency before the check." });
      setShowCaps(false);
    } catch { toast({ title: "Could not save the ceilings" }); }
    finally { setSavingCaps(false); }
  };

  const approve = async (d) => {
    setBusy(d.id + "approve");
    try { await api.post(`/admin/deals/${d.id}/approve`); toast({ title: "Deal approved & live", description: d.title }); await refresh(); }
    catch { toast({ title: "Action failed" }); }
    finally { setBusy(""); }
  };

  const recategorize = async (d, cat) => {
    if (!cat || cat === d.category) return;
    setBusy(d.id + "cat");
    try {
      const { data } = await api.post(`/admin/deals/${d.id}/category`, { category: cat });
      toast({ title: "Category changed", description: `${d.title} → ${cat}${data.shortDatedCleared ? " · short-dated cleared" : ""}` });
      await refresh();
    } catch (e) { toast({ title: "Could not change category", description: e.response?.data?.detail }); }
    finally { setBusy(""); }
  };

  const CAT_NAMES = categories.map((c) => c.name);

  const actions = (d) => (
    <>
      <label className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground" title="Change category">
        <Tags size={13} className="text-[#16a34a]" />
        <select value={d.category || ""} onChange={(e) => recategorize(d, e.target.value)} disabled={busy === d.id + "cat"}
          data-testid={`review-category-${d.id}`}
          className="text-xs font-semibold border border-border rounded-xl px-2 py-2 bg-card outline-none focus:ring-2 focus:ring-[#16a34a]/40 disabled:opacity-60">
          {CAT_NAMES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      {d.pendingApproval && (
        <button onClick={() => approve(d)} disabled={busy === d.id + "approve"} data-testid={`review-approve-${d.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] text-white rounded-xl px-3 py-2 transition-colors">
          {busy === d.id + "approve" ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Approve
        </button>
      )}
      {(d.flags || []).length > 0 && !d.hidden && !d.pendingApproval && (
        <button onClick={() => clearFlags(d.id)} disabled={busy === d.id + "ok"} data-testid={`review-ok-${d.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors">
          {busy === d.id + "ok" ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} className="text-[#16a34a]" />} Looks fine
        </button>
      )}
      {d.hidden && !d.pendingApproval ? (
        <button onClick={() => restore(d)} disabled={busy === d.id + "unhide"} data-testid={`review-restore-${d.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:bg-secondary rounded-xl px-3 py-2 transition-colors">
          {busy === d.id + "unhide" ? <Loader2 className="animate-spin" size={13} /> : <Eye size={13} />} Restore
        </button>
      ) : (
        <button onClick={() => { setPullFor(d); setPullReason(""); }} disabled={busy === d.id + "hide"} data-testid={`review-hide-${d.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold bg-rose-500 hover:bg-rose-600 text-white rounded-xl px-3 py-2 transition-colors">
          {busy === d.id + "hide" ? <Loader2 className="animate-spin" size={13} /> : <EyeOff size={13} />} Archive
        </button>
      )}
      <button onClick={() => setDeleteFor(d)} disabled={busy === d.id + "delete"} data-testid={`review-delete-${d.id}`}
        title="Delete permanently" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-rose-300 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-xl px-3 py-2 transition-colors">
        {busy === d.id + "delete" ? <Loader2 className="animate-spin" size={13} /> : <Trash2 size={13} />} Delete
      </button>
    </>
  );

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-5" data-testid="admin-deal-review">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-foreground flex items-center gap-2"><ShieldCheck size={18} className="text-[#16a34a]" /> Deal quality</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Deals publish instantly — these are the ones worth a second look, plus search to pull any live deal.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={toggleCaps} data-testid="price-caps-toggle"
            className={`inline-flex items-center gap-2 text-xs font-bold rounded-xl px-3 py-2 transition-colors ${capsOn ? "bg-[#16a34a] text-white" : "border border-border text-muted-foreground hover:bg-secondary"}`}>
            <DollarSign size={13} /> Price caps: {capsOn ? "ON" : "OFF"}
          </button>
          <button onClick={() => setShowCaps((v) => !v)} data-testid="price-caps-edit-toggle"
            className="inline-flex items-center gap-2 text-xs font-bold rounded-xl px-3 py-2 border border-border text-muted-foreground hover:bg-secondary transition-colors">
            {showCaps ? "Hide caps" : "Edit caps"}
          </button>
          <button onClick={toggleAi} data-testid="ai-moderation-toggle"
            className={`inline-flex items-center gap-2 text-xs font-bold rounded-xl px-3 py-2 transition-colors ${aiOn ? "bg-[#16a34a] text-white" : "border border-border text-muted-foreground hover:bg-secondary"}`}>
            <Sparkles size={13} /> AI content check: {aiOn ? "ON" : "OFF"}
          </button>
        </div>
      </div>

      {showCaps && (
        <div className="rounded-xl border border-border bg-secondary/30 p-4" data-testid="price-caps-editor">
          <p className="text-xs text-muted-foreground mb-3">
            Maximum price allowed per category, in <strong>USD</strong>. Each deal's price is converted
            from its own currency (live FX) before the check, so shops in any country are judged fairly.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.keys(caps).sort().map((cat) => (
              <label key={cat} className="text-xs font-semibold text-foreground">
                {cat}
                <div className="mt-1 flex items-center gap-1 rounded-lg border border-border bg-card px-2">
                  <span className="text-muted-foreground">$</span>
                  <input type="number" min="0" value={caps[cat]} data-testid={`price-cap-input-${cat}`}
                    onChange={(e) => setCaps((c) => ({ ...c, [cat]: e.target.value }))}
                    className="w-full bg-transparent py-2 text-sm outline-none" />
                </div>
              </label>
            ))}
            <label className="text-xs font-semibold text-foreground">
              Any other category
              <div className="mt-1 flex items-center gap-1 rounded-lg border border-border bg-card px-2">
                <span className="text-muted-foreground">$</span>
                <input type="number" min="0" value={capsDefault} data-testid="price-cap-input-default"
                  onChange={(e) => setCapsDefault(e.target.value)}
                  className="w-full bg-transparent py-2 text-sm outline-none" />
              </div>
            </label>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button onClick={saveCaps} disabled={savingCaps} data-testid="price-caps-save"
              className="inline-flex items-center gap-2 text-xs font-bold rounded-xl px-4 py-2 bg-[#16a34a] hover:bg-[#15803d] text-white transition-colors disabled:opacity-60">
              {savingCaps && <Loader2 size={13} className="animate-spin" />} Save ceilings
            </button>
            <button onClick={() => setShowCaps(false)} className="text-xs font-bold rounded-xl px-4 py-2 border border-border text-muted-foreground hover:bg-secondary transition-colors">Cancel</button>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-bold text-foreground mb-2">Waiting for your approval ({pending.length})</h3>
        {pending.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="pending-empty">Nothing waiting — no shop is on watch right now.</p>
        ) : (
          <div className="space-y-2" data-testid="pending-queue">
            {pending.map((d) => <DealRow key={d.id} d={d}>{actions(d)}</DealRow>)}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-bold text-foreground mb-2">Needs a look ({flagged.length})</h3>
        {flagged.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="flagged-empty">Nothing flagged — everything posted looks normal.</p>
        ) : (
          <div className="space-y-2" data-testid="flagged-queue">
            {flagged.map((d) => <DealRow key={d.id} d={d}>{actions(d)}</DealRow>)}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-bold text-foreground mb-2">Find & pull any deal</h3>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by deal title or business"
            data-testid="admin-deal-search"
            className="w-full bg-secondary rounded-xl pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#16a34a]/40" />
        </div>
        {results.length > 0 && (
          <div className="space-y-2 mt-3" data-testid="admin-deal-results">
            {results.slice(0, 20).map((d) => <DealRow key={d.id} d={d}>{actions(d)}</DealRow>)}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
          <History size={15} className="text-[#16a34a]" /> Recent category changes ({recats.length})
        </h3>
        {recats.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="recats-empty">No category changes yet.</p>
        ) : (
          <div className="space-y-2" data-testid="recats-list">
            {recats.map((r) => (
              <div key={r.id} data-testid={`recat-row-${r.dealId}`}
                className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-border px-3 py-2 text-xs">
                <Link to={`/deal/${r.dealId}`} className="font-bold text-foreground hover:text-[#16a34a] truncate max-w-[220px]">{r.title || "Deal"}</Link>
                <span className="text-muted-foreground">· {r.business || "—"}</span>
                <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                  <span className="rounded-full bg-secondary px-2 py-0.5">{r.from || "—"}</span>
                  <ArrowRight size={12} className="text-muted-foreground" />
                  <span className="rounded-full bg-[#16a34a]/12 text-[#15803d] px-2 py-0.5">{r.to}</span>
                </span>
                <span className="text-muted-foreground ml-auto">
                  {r.adminEmail} · {r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog open={!!pullFor} onOpenChange={(o) => !o && setPullFor(null)}>
        <DialogContent data-testid="pull-deal-dialog">
          <DialogHeader><DialogTitle>Archive “{pullFor?.title}”</DialogTitle></DialogHeader>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Why? (emailed to {pullFor?.business})</label>
            <div className="mt-2"><ArchiveReasonPicker picked={pullPicked} onToggle={togglePullReason} testidPrefix="pull-reason" /></div>
            <textarea value={pullReason} onChange={(e) => setPullReason(e.target.value)} rows={2} data-testid="pull-deal-reason"
              placeholder="Anything to add? (optional)"
              className="mt-2 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none resize-none" />
            {composeReason(pullPicked, pullReason) && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="pull-reason-preview">
                <span className="font-semibold text-foreground">They'll be told:</span> {composeReason(pullPicked, pullReason)}
              </p>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setPullFor(null)} className="text-sm font-semibold px-4 py-2 rounded-xl border border-border hover:bg-secondary">Cancel</button>
            <button onClick={() => pull(composeReason(pullPicked, pullReason))} data-testid="pull-deal-confirm"
              className="text-sm font-bold px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white">Archive deal</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <DialogContent data-testid="delete-deal-dialog">
          <DialogHeader><DialogTitle>Delete “{deleteFor?.title}” for good?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This wipes the deal off LytePress immediately — {deleteFor?.business} loses it, along with its page and any
            shared links. Views, grabs and redemption history stay in the records. You'll get one chance to undo it right
            after; use <strong>Archive</strong> instead if you only want it out of sight.
          </p>
          <DialogFooter>
            <button onClick={() => setDeleteFor(null)} data-testid="delete-deal-cancel" className="text-sm font-semibold px-4 py-2 rounded-xl border border-border hover:bg-secondary">Cancel</button>
            <button onClick={destroy} data-testid="delete-deal-confirm"
              className="text-sm font-bold px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white">Yes, delete it</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDealReview;
